import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export interface QuestionTitleContextValue {
  getTitleRef: (questionId: string) => HTMLElement | null;
  registerTitleRef: <T extends HTMLElement>(questionId: string, node: T | null) => void;
  unregisterTitleRef: (questionId: string) => void;
  isGroupPage: boolean;
}

const QuestionTitleContext = React.createContext<QuestionTitleContextValue | undefined>(undefined);

export function QuestionTitleProvider({ children }: { children: ReactNode }) {
  const questionIdToTitleRefMap = useRef<Record<string, HTMLElement | null>>({});
  const [titleRefsCount, setTitleRefsCount] = useState(0);

  const getTitleRef = useCallback((questionId: string) => {
    return questionIdToTitleRefMap.current[questionId] ?? null;
  }, []);

  const registerTitleRef = useCallback(<T extends HTMLElement>(questionId: string, node: T | null) => {
    questionIdToTitleRefMap.current[questionId] = node;
    setTitleRefsCount(Object.keys(questionIdToTitleRefMap.current).length);
  }, []);

  const unregisterTitleRef = useCallback((questionId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete questionIdToTitleRefMap.current[questionId];
    setTitleRefsCount(Object.keys(questionIdToTitleRefMap.current).length);
  }, []);

  const isGroupPage = useMemo(() => {
    return titleRefsCount > 1;
  }, [titleRefsCount]);

  const contextValue = useMemo(
    () => ({ getTitleRef, registerTitleRef, unregisterTitleRef, isGroupPage }),
    [isGroupPage],
  );

  return <QuestionTitleContext.Provider value={contextValue}>{children}</QuestionTitleContext.Provider>;
}

export function useQuestionTitleContext() {
  const value = useContext(QuestionTitleContext);
  if (!value) throw new Error('QuestionTitleContext is required!');

  return value;
}

export function useRegisterTitleRef(questionId: string) {
  const value = useContext(QuestionTitleContext);
  if (!value) throw new Error('QuestionTitleContext is required!');

  useEffect(() => {
    return () => {
      value.unregisterTitleRef(questionId);
    };
  }, [questionId]);

  const registerTitleRef = useCallback(
    <T extends HTMLElement>(node: T | null) => {
      value.registerTitleRef(questionId, node);
    },
    [questionId],
  );

  return { registerTitleRef };
}
